import {useState, useEffect} from 'react';
import {useColorModeValue} from '@chakra-ui/react';
import {telephonicService} from '../../services/telephonicService';
import {TranscriptTab} from '../common/SessionDetailView';
import {RECAP_OPALITE_BLUE} from '../common/recapButtonStyles';

// Map a company phone call's transcript turns onto the shared transcript
// message shape so they render through the exact same MessageBubble the
// regular encounter detail uses (bordered card + uppercase speaker label +
// timestamp). Exported so PhoneCallDetailPanel (which feeds SessionDetailView
// directly) and this component stay in lockstep.
export function mapPhoneMessages(rawMessages: any[]) {
  return (Array.isArray(rawMessages) ? rawMessages : [])
    .map((msg: any, idx: number) => {
      const text = msg.transcript || msg.output_text || msg.input_text || '';
      const isHuman = msg.is_user === true && msg.isAgentMessage !== true;
      return {
        id: msg.id || `phone-msg-${idx}`,
        timestamp: msg.timestamp,
        is_doctor: false,
        input_text: text,
        output_text: '',
        explicit_meaning: isHuman ? 'Caller' : 'Translation',
      };
    })
    .filter(msg => msg.input_text);
}

// Loads and renders a company phone call's transcript using the shared
// TranscriptTab so it is visually identical to the regular encounter
// transcript. Used inside the telephone-encounter detail view.
const PhoneCallTranscript = ({callId}: {callId: string | null | undefined}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const bodyText = useColorModeValue('gray.900', 'whiteAlpha.900');
  const mutedPanelBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!callId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    telephonicService
      .getTelephonicConversation(callId)
      .then(conv => {
        if (cancelled) return;
        setMessages(mapPhoneMessages(conv?.messages));
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[PhoneCallTranscript] failed to load transcript', err);
        setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  return (
    <TranscriptTab
      transcriptMessages={messages}
      loadingMessages={loading}
      cardBg={cardBg}
      borderColor={borderColor}
      subtleText={subtleText}
      accentColor={RECAP_OPALITE_BLUE}
      bodyText={bodyText}
      mutedPanelBg={mutedPanelBg}
    />
  );
};

export default PhoneCallTranscript;
